// When a message counts as read, and when to tell the server.
//
// The rule is deliberately strict, and the reason is evidentiary rather than cosmetic.
//
// Marking everything read the moment a thread opens — what WhatsApp does — claims a sixty-message
// backlog was read in 200ms. In an election dispute that claim is a record, and it is false. A
// read receipt here says "a human looked at this", so it has to be earned.
//
// Pure and clock-injected: the DOM parts (IntersectionObserver, visibility, focus) are thin
// wrappers around these decisions, and these are what can actually be wrong.

/** A bubble must be at least this visible to count as looked at. */
export const VISIBILITY_THRESHOLD = 0.5;

/** …and stay that visible for this long. A flick-scroll past does not count. */
export const DWELL_MS = 400;

/** Flush cadence. Whichever comes first. */
export const FLUSH_INTERVAL_MS = 1_000;
export const FLUSH_BATCH_SIZE = 20;

export interface IReadContext {
	/** document.visibilityState === 'visible' */
	documentVisible: boolean;
	/** The window has focus — a visible but unfocused tab is not being read. */
	windowFocused: boolean;
	/** This thread is the one on screen. */
	threadActive: boolean;
}

/**
 * All three must hold. Any one of them false means nobody is looking, however much of the
 * bubble is technically in the viewport.
 */
export const canRegisterReads = (context: IReadContext): boolean =>
	context.documentVisible && context.windowFocused && context.threadActive;

/**
 * Whether a bubble has been looked at long enough to count.
 *
 * @param visibleSince epoch ms when it crossed the visibility threshold, or null if it is not
 *        currently visible enough.
 */
export const hasDwelled = (visibleSince: number | null, now: number): boolean =>
	visibleSince !== null && now - visibleSince >= DWELL_MS;

/**
 * The high-water mark to report.
 *
 * <p>A single number rather than a list of ids: smaller, naturally idempotent, and it cannot
 * half-apply. Reporting the MAXIMUM dwelled sequence also means an event lost in flight is
 * subsumed by the next one rather than leaving a permanent hole.</p>
 *
 * <p>Returns null when there is nothing new to report — the caller must not send, because
 * clients re-evaluate on every scroll frame and a no-op POST per frame is a denial of service
 * you inflict on yourself.</p>
 */
export const nextHighWater = (dwelledSequences: number[], alreadyReported: number): number | null => {
	if (dwelledSequences.length === 0) return null;

	const highest = Math.max(...dwelledSequences);
	return highest > alreadyReported ? highest : null;
};

/**
 * Whether to flush now.
 *
 * <p>Batched because a fast scroll through a long thread would otherwise fire one request per
 * bubble. The size trigger keeps a burst from waiting a full second; the time trigger keeps a
 * single message from waiting for nineteen friends.</p>
 */
export const shouldFlush = (pendingCount: number, msSinceLastFlush: number): boolean =>
	pendingCount >= FLUSH_BATCH_SIZE || (pendingCount > 0 && msSinceLastFlush >= FLUSH_INTERVAL_MS);

/**
 * Sequence of the last own message the other side has read, for the single `Read HH:MM` line.
 *
 * <p>Rendered ONCE under the newest read message rather than as a glyph on every bubble.
 * A wall of blue ticks carries exactly the same information as one high-water line and reads as
 * noise — Intercom gets this right where WhatsApp does not.</p>
 *
 * <p>Returns null when none have been read, so the caller renders nothing rather than a line
 * claiming a read that has not happened.</p>
 */
export const lastReadOwnSequence = (messages: { sequence: number; own: boolean; readByOther?: boolean }[]): number | null => {
	const read = messages.filter((m) => m.own && m.readByOther).map((m) => m.sequence);
	return read.length ? Math.max(...read) : null;
};
